import { isTestnetPremiumOnly } from '@ankr.com/chains-list';
import { useEffect, useMemo } from 'react';

import { SecondaryTabs } from 'modules/common/components/SecondaryTabs';
import { getPrivateChainTypeTabs } from 'modules/chains/utils/getPrivateChainTypeTabs';
import { isMainnetHidden } from 'modules/chains/utils/isMainnetHidden';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useTabs } from 'modules/common/hooks/useTabs';

import { useChainSelectors } from '../ChainSelectorsProvider';
import { useChainTypeTabsStyles } from './useChainTypeTabsStyles';

interface ChainTypeTabsProps {
  canTabBeBlocked?: boolean;
  className?: string;
  classNameTab?: string;
  classNameTabsInner?: string;
  classNameTabsWrapper?: string;
  onBlockedTabClick?: () => void;
}

export const ChainTypeTabs = ({
  canTabBeBlocked = false,
  className,
  classNameTab,
  classNameTabsInner,
  classNameTabsWrapper,
  onBlockedTabClick,
}: ChainTypeTabsProps) => {
  const { chain, chainType, groupedEndpoints, setChainType } =
    useChainSelectors();

  const { isPremium } = useAccountStatus();

  const tabs = useMemo(
    () =>
      getPrivateChainTypeTabs({
        canTabBeBlocked,
        endpoints: groupedEndpoints,
        isBlockedMainnet: !isPremium && chain?.isMainnetPremiumOnly,
        isBlockedTestnet: !isPremium && isTestnetPremiumOnly(chain),
        isHiddenMainnet: isMainnetHidden(chain),
        onBlockedTabClick,
        onClick: id => setChainType(id),
      }).filter(tab => !tab.isHidden),
    [
      canTabBeBlocked,
      chain,
      groupedEndpoints,
      isPremium,
      onBlockedTabClick,
      setChainType,
    ],
  );

  const [chainTypeTabs, chainTypeTab] = useTabs({
    initialTabID: chainType,
    tabs,
  });

  // this effect fixes case when selected chain type is not added to the project
  // for example, when the user is switching projects in the copy endpoint dialog on the chains list page
  // and after switch we can't find the selected chain type
  // we should switch to the first available chain type
  useEffect(() => {
    // if chainType is not found in tabs (tab.id)
    if (tabs.findIndex(tab => tab.id === chainType) === -1) {
      // we should select the first type available in tabs
      setChainType(tabs[0]?.id);
    }

    // should be triggered only on selected project change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedEndpoints]);

  const { classes, cx } = useChainTypeTabsStyles();

  return (
    <SecondaryTabs
      className={cx(classes.root, className)}
      classNameTab={classNameTab}
      classNameTabsInner={classNameTabsInner}
      classNameTabsWrapper={classNameTabsWrapper}
      selectedTab={chainTypeTab}
      tabs={chainTypeTabs}
    />
  );
};
